import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { ChevronRight, FolderOpenDot, RotateCw, TriangleAlert } from "lucide-react";

// Choosing an engagement needs its identity, not its quality gates. /engagements evaluates all
// four gates for every engagement it returns, which on real data took over a minute; /picker is
// the same list under the same authorization with none of that work. Gate status belongs on the
// engagement's own dashboard, where it is computed once for the one engagement being looked at.
export default function EngagementPicker({ title, subtitle, tabKey, breadcrumb }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setError(null);
    setItems(null);
    api.get("/engagements/picker")
      .then((r) => setItems(r.data))
      // A failed request used to render as an empty list, so a backend that was down looked
      // exactly like a firm with no engagements. Say which one it is.
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Gagal memuat perikatan"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const go = (id) => navigate(`/engagements/${id}?tab=${tabKey}`);

  return (
    <Layout>
      <PageHeader title={title} subtitle={subtitle} breadcrumb={breadcrumb || "Audit OS"} />
      <div className="p-6">
        <div className="text-xs text-muted-foreground mb-3">Pilih perikatan untuk membuka modul <span className="font-medium text-foreground">{title}</span>. Semua modul terhubung ke perikatan, basis data, versioning, dan gerbang kualitas yang sama.</div>

        {items === null && !error && <div className="text-sm text-muted-foreground" data-testid="picker-loading">Memuat...</div>}

        {error && (
          <div className="bg-white border border-red-300 rounded-sm p-6 text-sm" data-testid="picker-error">
            <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
              <TriangleAlert size={16} /> Daftar perikatan tidak dapat dimuat
            </div>
            <div className="text-muted-foreground mb-3">{error}</div>
            <div className="text-[11px] text-muted-foreground mb-3">Ini bukan berarti tidak ada perikatan — daftar tidak berhasil diambil dari server.</div>
            <button onClick={load} data-testid="picker-retry"
              className="inline-flex items-center gap-1.5 text-xs font-medium border border-border rounded-sm px-3 py-1.5 hover:border-slate-800">
              <RotateCw size={13} /> Coba lagi
            </button>
          </div>
        )}

        {items && items.length === 0 && !error && (
          <div className="bg-white border border-border rounded-sm p-10 text-center text-sm text-muted-foreground" data-testid="picker-empty">
            Belum ada perikatan. Buat perikatan terlebih dahulu di menu <span className="font-medium">Klien</span>.
          </div>
        )}

        <div className="grid gap-2" data-testid="engagement-picker-list">
          {(items || []).map((e) => (
            <button key={e.id} onClick={() => go(e.id)} data-testid={`picker-eng-${e.id}`}
              className="w-full text-left bg-white border border-border rounded-sm px-4 py-3 flex items-center gap-3 hover:border-slate-800 transition-colors group">
              <FolderOpenDot size={18} className="text-slate-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{e.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{[e.client_name, e.period, e.framework].filter(Boolean).join(" · ")}</div>
              </div>
              {e.phase && <span className="text-[10px] px-2 py-0.5 border border-border rounded-sm font-semibold text-slate-600" data-testid={`picker-phase-${e.id}`}>{e.phase}</span>}
              <ChevronRight size={16} className="text-muted-foreground group-hover:text-slate-800" />
            </button>
          ))}
        </div>
      </div>
    </Layout>
  );
}
